import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { guidFor } from '@ember/object/internals';
import { task, timeout } from 'ember-concurrency';
import { number2Currency } from 'beebee-base/lib/utility';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<input\n  id={{this.inputId}}\n  type=\'text\'\n  placeholder={{this.placeholder}}\n  value={{this._value}}\n  disabled={{@disabled}}\n  name={{@name}}\n  autocomplete=\'off\'\n  aria-invalid={{if (or @errorMsg @error) \'true\'}}\n  aria-describedby={{if (or @errorMsg @error) (concat this.inputId \'-error\')}}\n  class=\'{{@class}} {{if @disabled \"disabled\"}} {{if @errorMsg \"error\"}} {{if @error \"error\"}}\'\n  data-test-focus-in\n  ...attributes\n  {{did-insert this.onInsert}}\n  {{did-update this.onExternalChange @value}}\n  {{on \'focusin\' this.focusIn}}\n  {{on \'focusout\' this.focusOut}}\n  {{on \'keydown\' this.keyDown}}\n  {{on \'keyup\' this.keyUp}}\n/>\n{{#if (or @errorMsg @error)}}\n  <span id={{concat this.inputId \'-error\'}} role=\'alert\' class=\'input-error\'>\n    {{or @errorMsg @error}}\n  </span>\n{{/if}}");

var _dec, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _BbInputCurrencyComponent;
let BbInputCurrencyComponent = (_dec = task(function* () {
  yield timeout(50);
  if (this.isDestroying || this.isDestroyed) return;
  const el = document.getElementById(this.inputId);
  if (!el || el.value === '') return;
  const currency_value = number2Currency(el.value);
  el.value = currency_value;
  this._value = currency_value;
}).restartable(), _class = (_BbInputCurrencyComponent = class BbInputCurrencyComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    _initializerDefineProperty(this, "notify", _descriptor2, this);
    _initializerDefineProperty(this, "_value", _descriptor3, this);
    _initializerDefineProperty(this, "_isEditing", _descriptor4, this);
    _initializerDefineProperty(this, "initCurrencyTask", _descriptor5, this);
  }
  get inputId() {
    return `input-currency-${guidFor(this)}`;
  }
  get placeholder() {
    return this.args.wrapPlaceholder || '10,00';
  }
  onInsert() {
    this.initCurrencyTask.perform();
  }

  // Chiamato da did-update quando @value cambia dal parent (dati async, reset form, ecc.)
  onExternalChange() {
    if (this._isEditing) return; // non interrompere l'utente mentre digita
    this._value = this.args.value;
    this.initCurrencyTask.perform();
  }
  _formatCurrency(input, blur) {
    let input_val = input.value;

    // Numpad / EN-style decimal: a single dot followed by 0-2 digits and no
    // comma is a decimal separator ("10.5" means 10,50, "10." keeps typing) —
    // without this it was parsed as a thousands dot and became 105,00 (10x
    // corruption). A dot followed by 3 digits ("1.234") keeps the legacy
    // thousands meaning. \d{0,2} normalizes a trailing dot immediately so the
    // user can keep entering decimals.
    if (!input_val.includes(',') && /^-?\d+\.\d{0,2}$/.test(input_val)) {
      input_val = input_val.replace('.', ',');
      input.value = input_val;
    }
    if (input_val === '') {
      this._value = '';
      this.args.action?.(null);
      return;
    }
    if (input_val === '-') {
      this._value = '-';
      return;
    }
    const original_len = input_val.length;
    let caret_pos = input.selectionStart;
    if (input_val.indexOf(',') >= 0) {
      const decimal_pos = input_val.indexOf(',');
      let left_side = input_val.substring(0, decimal_pos);
      let right_side = input_val.substring(decimal_pos);
      left_side = BbInputCurrencyComponent._formatNumber(left_side);
      right_side = BbInputCurrencyComponent._formatNumber(right_side);
      if (blur === 'blur') {
        right_side += '00';
      }
      right_side = right_side.substring(0, 2);
      input_val = `${left_side},${right_side}`;
    } else {
      input_val = BbInputCurrencyComponent._formatNumber(input_val);
      if (blur === 'blur') {
        input_val += ',00';
      }
    }
    input.value = input_val;
    this._value = input_val;
    if (blur !== 'blur') {
      const updated_len = input_val.length;
      caret_pos = updated_len - original_len + caret_pos;
      input.focus();
      input.setSelectionRange(caret_pos, caret_pos);
    }
    this.args.action?.(input_val);
  }
  focusIn() {
    this._isEditing = true;
  }
  focusOut(e) {
    this._isEditing = false;
    this._formatCurrency(e.target, 'blur');
    this.args.actionFocusOut?.();
  }
  keyDown(e) {
    const key = e?.key;
    if (!key || key.length !== 1) return true;

    // Let keyboard shortcuts through (Ctrl/Cmd+V/C/A, etc.)
    if (e.ctrlKey || e.metaKey) return true;

    // Allow: digits, comma, dot (numpad/EN decimal — normalized to comma in
    // _formatCurrency), minus
    if (/[0-9,.\-]/.test(key)) return true;

    // Blocca tutto il resto (lettere, simboli)
    e.preventDefault();
    this.notify.warning(this.intl.t('use comma for decimals'));
    return false;
  }
  keyUp(e) {
    this._formatCurrency(e.target);
  }
}, _BbInputCurrencyComponent._formatNumber = n => n.replace(/(?!-)[^0-9]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.'), _BbInputCurrencyComponent), _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "notify", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_value", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.value;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "_isEditing", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "initCurrencyTask", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "onInsert", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onInsert"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onExternalChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onExternalChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "focusIn", [action], Object.getOwnPropertyDescriptor(_class.prototype, "focusIn"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "focusOut", [action], Object.getOwnPropertyDescriptor(_class.prototype, "focusOut"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "keyDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "keyDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "keyUp", [action], Object.getOwnPropertyDescriptor(_class.prototype, "keyUp"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbInputCurrencyComponent);

export { BbInputCurrencyComponent as default };
//# sourceMappingURL=bb-input-currency.js.map
